import { useState } from 'react';
import type { ReactNode } from 'react';
import { ExternalLink, Loader2, Search } from 'lucide-react';
import { BaseSheet } from '@/components/common/BaseSheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useStripeCustomerInspection } from '@/hooks/users/useUserManagement';
import { cn } from '@/lib/utils';
import type { StripeCustomerInspection, StripeMode } from '@/services/billing/SubscriptionService';
import { CopyableJsonBlock } from '../../BrainChatLogs/CopyableJsonBlock';
import { getSubscriptionStatusBadge } from '../utils/userBadges';

interface StripeCustomerLookupSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAccount: (email: string) => void;
}

const formatMoney = (amount: number | null | undefined, currency: string | null | undefined) => {
  if (amount === null || amount === undefined || !currency) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
};

const formatDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');

const stripeUrl = (mode: StripeMode, path: string) =>
  `https://dashboard.stripe.com/${mode === 'test' ? 'test/' : ''}${path}`;

const Section = ({ title, count, children }: { title: string; count?: number; children: ReactNode }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
    <h3 className="text-sm font-semibold text-gray-700">
      {title}
      {count !== undefined && <span className="ml-1.5 font-normal text-gray-500">({count})</span>}
    </h3>
    {children}
  </div>
);

const Empty = ({ children }: { children: ReactNode }) => <p className="text-sm text-gray-500">{children}</p>;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="min-w-0">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 break-words">{children}</div>
  </div>
);

const StripeLink = ({ mode, path }: { mode: StripeMode; path: string }) => (
  <a
    href={stripeUrl(mode, path)}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center justify-center min-h-[32px] min-w-[32px] text-gray-400 hover:text-genie-primary transition-colors"
    title="Open in Stripe"
  >
    <ExternalLink className="h-4 w-4" />
  </a>
);

const InspectionResult = ({
  data,
  mode,
  onOpenAccount,
}: {
  data: StripeCustomerInspection;
  mode: StripeMode;
  onOpenAccount: (email: string) => void;
}) => {
  const { customer } = data;

  if (customer.deleted) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
        Stripe customer {customer.id} has been deleted.
      </div>
    );
  }

  const mirrorSubscriptions = data.mirror?.subscriptions ?? [];
  const unmirroredCount = data.subscriptions.filter((s) => !s.mirrored).length;

  return (
    <div className="space-y-4">
      <Section title="Customer">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">{customer.name || customer.email || customer.id}</div>
            {customer.name && customer.email && <div className="text-sm text-gray-500 truncate">{customer.email}</div>}
          </div>
          <StripeLink mode={mode} path={`customers/${customer.id}`} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Created">{formatDate(customer.createdAt)}</Field>
          <Field label="Currency">{customer.currency?.toUpperCase() || '—'}</Field>
          <Field label="Balance">{formatMoney(customer.balance, customer.currency)}</Field>
          <Field label="Phone">{customer.phone || '—'}</Field>
          <Field label="Delinquent">{customer.delinquent ? 'Yes' : 'No'}</Field>
          <Field label="Country">{customer.address?.country || '—'}</Field>
        </div>
        {data.paymentMethods.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {data.paymentMethods.map((method) => (
              <Badge key={method.id} variant="outline" className="text-xs font-normal">
                {method.brand || method.type} •••• {method.last4 || '—'}
                {method.expMonth && method.expYear ? ` · ${method.expMonth}/${method.expYear}` : ''}
                {method.id === customer.defaultPaymentMethodId ? ' · default' : ''}
              </Badge>
            ))}
          </div>
        )}
      </Section>

      <Section title="Help Genie account" count={data.accounts.length}>
        {data.accounts.length === 0 ? (
          <Empty>No account is linked to this customer or uses its email.</Empty>
        ) : (
          data.accounts.map((account) => (
            <div key={account.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{account.email || account.id}</div>
                <div className="text-xs text-gray-500 truncate">
                  {account.full_name ? `${account.full_name} · ` : ''}
                  {account.match === 'stripe_id'
                    ? 'Linked to this customer'
                    : `Same email only — linked to ${account.stripe_id || 'no Stripe customer'}`}
                </div>
              </div>
              {account.email && (
                <Button variant="outline" size="sm" onClick={() => onOpenAccount(account.email as string)}>
                  Open account
                </Button>
              )}
            </div>
          ))
        )}
      </Section>

      <Section title="Subscriptions" count={data.subscriptions.length}>
        {unmirroredCount > 0 && (
          <p className="text-xs text-amber-700">
            {unmirroredCount} {unmirroredCount === 1 ? "subscription isn't" : "subscriptions aren't"} in Help Genie.
          </p>
        )}
        {data.subscriptions.length === 0 ? (
          <Empty>No subscriptions in Stripe.</Empty>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.subscriptions.map((subscription) => (
              <div key={subscription.id} className="py-2.5 first:pt-0 last:pb-0 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {getSubscriptionStatusBadge(subscription.status)}
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {subscription.productName || subscription.priceId || subscription.id}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs font-normal',
                        subscription.mirrored ? 'text-green-700 border-green-200' : 'text-amber-700 border-amber-200',
                      )}
                    >
                      {subscription.mirrored ? 'In Help Genie' : 'Not in Help Genie'}
                    </Badge>
                    <StripeLink mode={mode} path={`subscriptions/${subscription.id}`} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
                  <span>
                    {formatMoney(subscription.unitAmount, subscription.currency)}
                    {subscription.interval ? ` / ${subscription.interval}` : ''} × {subscription.quantity}
                  </span>
                  <span>Since {formatDate(subscription.createdAt)}</span>
                  {subscription.currentPeriodEnd && <span>Renews {formatDate(subscription.currentPeriodEnd)}</span>}
                  {subscription.trialEnd && <span>Trial ends {formatDate(subscription.trialEnd)}</span>}
                  {subscription.cancelAt && <span>Cancels {formatDate(subscription.cancelAt)}</span>}
                  {subscription.canceledAt && <span>Canceled {formatDate(subscription.canceledAt)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Invoices" count={data.invoices.length}>
        {data.invoices.length === 0 ? (
          <Empty>No invoices.</Empty>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.invoices.map((invoice) => (
              <div key={invoice.id} className="py-2 first:pt-0 last:pb-0 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="text-gray-900 truncate">
                    {formatMoney(invoice.total, invoice.currency)}
                    <span className="text-gray-500"> · {invoice.number || invoice.id}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(invoice.createdAt)}
                    {invoice.billingReason ? ` · ${invoice.billingReason.replace(/_/g, ' ')}` : ''}
                    {invoice.amountDue > invoice.amountPaid && invoice.status === 'open'
                      ? ` · ${formatMoney(invoice.amountDue - invoice.amountPaid, invoice.currency)} due`
                      : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="outline" className="text-xs font-normal capitalize">{invoice.status || 'unknown'}</Badge>
                  <StripeLink mode={mode} path={`invoices/${invoice.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Payments" count={data.charges.length}>
        {data.charges.length === 0 ? (
          <Empty>No payments.</Empty>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.charges.map((charge) => (
              <div key={charge.id} className="py-2 first:pt-0 last:pb-0 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="text-gray-900 truncate">
                    {formatMoney(charge.amount, charge.currency)}
                    {charge.amountRefunded > 0 && (
                      <span className="text-amber-700"> · {formatMoney(charge.amountRefunded, charge.currency)} refunded</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {formatDate(charge.createdAt)}
                    {charge.card ? ` · ${charge.card.brand} •••• ${charge.card.last4}` : ''}
                    {charge.failureMessage ? ` · ${charge.failureMessage}` : charge.description ? ` · ${charge.description}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs font-normal capitalize',
                      charge.status === 'failed' && 'text-red-700 border-red-200',
                    )}
                  >
                    {charge.status}
                  </Badge>
                  <StripeLink mode={mode} path={`payments/${charge.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Help Genie billing records">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Subscriptions">{mirrorSubscriptions.length}</Field>
          <Field label="Call purchases">{data.mirror?.callPurchases.length ?? 0}</Field>
          <Field label="Phone numbers">{data.mirror?.phoneSubscriptions.length ?? 0}</Field>
        </div>
      </Section>

      <details className="rounded-xl border border-gray-200 bg-gray-50 p-5 [&_summary]:cursor-pointer">
        <summary className="text-sm font-semibold text-gray-700">Raw data</summary>
        <div className="mt-4">
          <CopyableJsonBlock label="Inspection" value={data} />
        </div>
      </details>
    </div>
  );
};

export const StripeCustomerLookupSheet = ({ isOpen, onClose, onOpenAccount }: StripeCustomerLookupSheetProps) => {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<StripeMode>('live');
  const [lookup, setLookup] = useState<{ customerId: string; mode: StripeMode } | null>(null);
  const { data, isFetching, error, refetch } = useStripeCustomerInspection(
    lookup?.customerId ?? null,
    lookup?.mode ?? 'live',
  );
  const trimmed = input.trim();

  const handleLookup = () => {
    if (!trimmed) return;
    if (lookup?.customerId === trimmed && lookup.mode === mode) {
      refetch();
      return;
    }
    setLookup({ customerId: trimmed, mode });
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Stripe Customer Lookup"
      description="Everything Stripe and Help Genie know about one Stripe customer. Read only."
      size="xl"
      footer={
        <div className="flex justify-start gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleLookup();
              }
            }}
            placeholder="cus_..."
            className="min-h-[44px] text-base sm:text-sm font-mono"
            autoFocus
          />
          <div className="flex rounded-md border border-gray-200 p-0.5 shrink-0">
            {(['live', 'test'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setMode(option)}
                className={cn(
                  'min-h-[40px] px-3 rounded text-sm capitalize transition-colors',
                  mode === option ? 'bg-genie-primary text-white' : 'text-gray-600 hover:bg-gray-100',
                )}
              >
                {option}
              </button>
            ))}
          </div>
          <Button
            type="button"
            onClick={handleLookup}
            disabled={!trimmed || isFetching}
            className="min-h-[44px] bg-genie-primary hover:bg-genie-primary/90 gap-2 shrink-0"
          >
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Look up
          </Button>
        </div>

        {isFetching && !data ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
            <span className="text-sm">Fetching from Stripe...</span>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            Couldn't load this customer: {(error as Error).message}
          </div>
        ) : data && lookup ? (
          <InspectionResult data={data} mode={lookup.mode} onOpenAccount={onOpenAccount} />
        ) : (
          <Empty>Enter a Stripe customer ID to see its profile, subscriptions and payments.</Empty>
        )}
      </div>
    </BaseSheet>
  );
};
